import type { ReactNode } from "react";

import { Tooltip, type LegendRow } from "@/components/ui";

/* ------------------------------- Table -------------------------------- */

// Table-scroll mode (driven by `data-table-mode` on <html>; see the
// table-* custom variants in globals.css). The page wrapper goes
// flex-column-full-height and the table region fills + scrolls internally
// when "sticky" (always) or "adaptive" (md+ only); otherwise both are
// normal-flow and the dashboard <main> scrolls the whole page.
const MODE_PAGE =
  "table-sticky:flex table-sticky:h-full table-sticky:flex-col " +
  "table-adaptive:md:flex table-adaptive:md:h-full table-adaptive:md:flex-col";
// The frame — the v2 card radius, like Panel — is drawn over the scroller by
// a box that doesn't scroll, never by the scroller itself: iOS Safari drops a
// scrolling box's own border once its content is wider than it, so a wide
// table lost its far edge, and its near one once scrolled. The scroller keeps
// the radius, so its overflow still clips the header's corners. The frame
// holds still through overscroll bounce too, where a header-drawn top rule
// would leave the sides connecting to nothing; the header draws only its
// bottom divider (see Th/MiniTh).
const FRAME =
  "relative isolate after:pointer-events-none after:absolute after:inset-0 " +
  "after:z-20 after:rounded-xl after:border after:border-line";
const MODE_FRAME_FILL =
  "table-sticky:flex table-sticky:min-h-0 table-sticky:flex-1 table-sticky:flex-col " +
  "table-adaptive:md:flex table-adaptive:md:min-h-0 table-adaptive:md:flex-1 table-adaptive:md:flex-col";
// A table that scrolls sideways only says so on both axes: `overflow-y: clip`
// beside `overflow-x: auto`. Left to compute as `auto`, the box is a scroll
// container up and down as well, and a pointer resting over the table swallows
// the wheel — the page underneath would not move. iPad Safari goes further:
// once a table is wider than its box, a trackpad swipe over it stays with it
// whatever the axis, so a page table is laid out to fit an iPad (see
// CategoryRow) rather than lean on the sideways scroll.
const SIDEWAYS = "overflow-x-auto overflow-y-clip";
const MODE_TABLE_FILL =
  "scroll-hold rounded-xl " +
  `table-normal:overflow-x-auto table-normal:overflow-y-clip ` +
  "table-sticky:min-h-0 table-sticky:flex-1 table-sticky:overflow-x-auto table-sticky:overflow-y-auto " +
  "table-adaptive:overflow-x-auto table-adaptive:overflow-y-clip " +
  "table-adaptive:md:min-h-0 table-adaptive:md:flex-1 table-adaptive:md:overflow-y-auto";
// The header's fill is one box, the <thead>, not a fill per cell: WebKit
// snaps each cell's background to whole device pixels, and where two meet on
// a fractional column edge a hairline of the page shows through. The pin
// rides the <thead> too, for a header of page-table cells (see Th): sticky
// holds on a header group in every engine the office runs in, and a group
// pinned whole has no seams either.
const HEAD =
  "[&>thead]:bg-bg-muted " +
  "[&>thead:has([data-pin])]:sticky [&>thead:has([data-pin])]:top-0 [&>thead:has([data-pin])]:z-10";

export function Table({
  children,
  fill = false,
}: {
  children: ReactNode;
  /**
   * Full-height mode. The wrapper claims the remaining height of a
   * flex-column page (`flex-1`) and scrolls internally, so the sticky
   * <Th> stays pinned and ONLY the table scrolls — no page/body double
   * scrollbar, and no hard-coded height offset. Use on index pages whose
   * primary content is one table: wrap the page in `flex h-full
   * flex-col`, keep the heading + toolbar in a `shrink-0` zone, and put
   * `<Table fill>` last.
   *
   * Without `fill` the table flows naturally and the dashboard's <main>
   * scrolls the whole page (still a single scrollbar). `overflow-x-auto`
   * keeps wide tables horizontally scrollable on narrow viewports
   * (Safari otherwise ran rows off the page).
   */
  fill?: boolean;
}) {
  return (
    <div className={fill ? `${FRAME} ${MODE_FRAME_FILL}` : FRAME}>
      <div
        className={
          fill ? MODE_TABLE_FILL : `scroll-hold ${SIDEWAYS} rounded-xl`
        }
      >
        <table className={`w-full border-collapse text-left text-sm ${HEAD}`}>
          {children}
        </table>
      </div>
    </div>
  );
}

/**
 * Full-height layout for an index page whose primary content is one
 * table. A fixed header zone (heading + toolbar) sits above a table that
 * fills the rest of the office <main> and scrolls internally — so the
 * table, not the page, scrolls (no double scrollbar; no height guesses).
 *
 * On phone landscape the header + toolbar hide, leaving just the table
 * and its pagination ("rotate to portrait for the rest"). Pagination is
 * the table's own nav, so it stays.
 *
 * Pass the table as the `<Table fill>` child (or an `<EmptyState>`); it
 * claims the remaining height.
 */
export function TablePageLayout({
  heading,
  toolbar,
  pagination,
  children,
}: {
  heading: ReactNode;
  /** Search / filter controls. Hidden alongside the heading on landscape. */
  toolbar?: ReactNode;
  /** The table's pager — kept visible on phone landscape. */
  pagination?: ReactNode;
  /** `<Table fill>` (or an `<EmptyState>`); fills the remaining height. */
  children: ReactNode;
}) {
  return (
    <div className={MODE_PAGE}>
      <div className="shrink-0">
        <div className="mobile-landscape:hidden">
          {heading}
          {toolbar}
        </div>
        {pagination}
      </div>
      {children}
    </div>
  );
}

/** The office's one time column: right-aligned under a right-aligned header,
 *  mono, xs, muted. Every table's timestamp column takes it through
 *  `variant="time"` on its {@link Th} and {@link Td}, whatever mode the
 *  timestamp inside reads in, so a time sits and reads the same on every page. */
const TIME_TH = "text-right";
const TIME_TD = "text-right font-mono text-xs text-fg-muted";

export function Th({
  children,
  className = "",
  variant = "default",
}: {
  children?: ReactNode;
  className?: string;
  /** `time` heads a timestamp column (see {@link Td}). */
  variant?: "default" | "time";
}) {
  return (
    // The header/body divider is an inset box-shadow, not a border: with
    // `border-collapse` a cell's own border belongs to the table grid and
    // scrolls away under a pinned header, while a shadow is part of the
    // cell's box and stays pinned with it. The table's top edge is its
    // frame (see Table) — the header doesn't draw its own top rule,
    // which would leave a gap when the pinned header drops during bounce.
    // `data-pin` asks the Table to pin the header group this cell heads; the
    // fill and the pin are the group's (see HEAD), not the cell's.
    <th
      data-pin=""
      className={`label whitespace-nowrap px-4 py-3 shadow-[inset_0_-1px_0_0_var(--color-line)] !text-fg-subtle ${variant === "time" ? TIME_TH : ""} ${className}`}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  className = "",
  colSpan,
  rowSpan,
  borderless = false,
  variant = "default",
}: {
  children?: ReactNode;
  className?: string;
  /** Span multiple columns (e.g. a full-width "empty" row). */
  colSpan?: number;
  /** Span multiple rows: a group's name down the rows it heads. */
  rowSpan?: number;
  /** Drop the hairline bottom rule (e.g. a standalone "empty" row). */
  borderless?: boolean;
  /** `time` is a timestamp column's cell: the office's one time style. */
  variant?: "default" | "time";
}) {
  return (
    <td
      colSpan={colSpan}
      rowSpan={rowSpan}
      className={`whitespace-nowrap px-4 py-3 align-middle ${variant === "time" ? TIME_TD : "text-fg"} ${borderless ? "" : "border-b border-line"} ${className}`}
    >
      {children}
    </td>
  );
}

/**
 * The "no rows" row for an office {@link Table}: a single full-width, centered,
 * muted message so an empty list still shows the table's column header + a quiet
 * line below it, instead of swapping the whole table for a dashed box. Keep it
 * link-free — it states the empty fact, it doesn't offer an action. Render it as
 * the sole `<tbody>` child when there are zero rows; `colSpan` must match the
 * table's column count. One component for every list page ("fix once, fix
 * everywhere").
 */
export function TableEmpty({
  colSpan,
  children,
}: {
  colSpan: number;
  children: ReactNode;
}) {
  return (
    <tr>
      <td
        colSpan={colSpan}
        className="px-4 py-16 text-center text-sm text-fg-muted"
      >
        {children}
      </td>
    </tr>
  );
}

/**
 * Header cell for an in-section "mini" table (a reservation's items, an item's
 * assets) — the quiet `.label` styling, NOT bold, hairlines above + below,
 * padded to line up with {@link Td}; the muted header fill is the header
 * group's (see Table). Unlike the page-table {@link Th} it asks for no pin
 * (that's for full-height scroll tables); a real `border-b` is fine since it
 * never scrolls under a pinned header. The top rule is the table's top edge
 * (its frame draws it). One component for every section table ("fix once,
 * fix everywhere").
 */
export function MiniTh({
  children,
  className = "",
  variant = "default",
}: {
  children?: ReactNode;
  className?: string;
  /** `time` heads a timestamp column (see {@link Td}). */
  variant?: "default" | "time";
}) {
  return (
    <th
      className={`label whitespace-nowrap border-b border-line px-4 py-3 font-normal !text-fg-subtle ${variant === "time" ? TIME_TH : "text-left"} ${className}`}
    >
      {children}
    </th>
  );
}

/**
 * A header cell with a legend: the label, then an info mark that opens a
 * hover card naming each value the column can hold and what it means. The
 * card opens below, anchored to the cell so it clears the header's padding
 * and rule; it is placed against the window, so no sibling cell paints over
 * it. Keyboard reaches it — the mark is focusable.
 */
export function LegendHeader({
  label,
  rows,
  className = "",
}: {
  label: string;
  rows: readonly LegendRow[];
  className?: string;
}) {
  return (
    <Th className={className}>
      <span className="inline-flex items-baseline gap-1.5">
        {label}
        <Tooltip title={label} rows={rows} placement="below" anchor="container">
          <span
            tabIndex={0}
            className="cursor-help text-fg-subtle transition-colors hover:text-fg focus:text-fg focus:outline-none"
          >
            <span aria-hidden>ⓘ</span>
            <span className="sr-only">{label} legend</span>
          </span>
        </Tooltip>
      </span>
    </Th>
  );
}
